import { APP_CONFIG } from "../config/appConfig";

// Admin configuration
export interface AdminConfig {
  isAdmin: boolean;
  permissions: AdminPermission[];
  lastLoginAsAdmin?: string;
}

export interface AdminPermission {
  resource: string;
  actions: string[];
}

// Default admin permissions
export const DEFAULT_ADMIN_PERMISSIONS: AdminPermission[] = [
  {
    resource: "orders",
    actions: ["read", "update", "download", "delete"],
  },
  {
    resource: "users",
    actions: ["read", "update"],
  },
  {
    resource: "files",
    actions: ["read", "download", "delete"],
  },
  {
    resource: "analytics",
    actions: ["read"],
  },
];

// Development mode toggle - allows simulating admin mode
let DEV_ADMIN_MODE = false;

/**
 * Check if user is an admin based on email whitelist
 */
export function isUserAdmin(userEmail: string | null | undefined): boolean {
  console.log(`🔍 Checking admin status for email: "${userEmail}"`);
  console.log(`🔍 DEV_ADMIN_MODE: ${DEV_ADMIN_MODE}`);
  console.log(`🔍 ADMIN_EMAILS:`, APP_CONFIG.ADMIN_EMAILS);

  if (!userEmail) {
    console.log(`❌ No email provided`);
    return false;
  }

  // Check development mode first
  if (DEV_ADMIN_MODE) {
    console.log(`✅ Admin access granted via DEV_ADMIN_MODE`);
    return true;
  }

  // Check email whitelist
  const normalizedEmail = userEmail.toLowerCase();
  const isInWhitelist = APP_CONFIG.ADMIN_EMAILS.includes(normalizedEmail);
  console.log(`🔍 Normalized email: "${normalizedEmail}"`);
  console.log(`🔍 Is in whitelist: ${isInWhitelist}`);

  if (isInWhitelist) {
    console.log(`✅ Admin access granted via email whitelist`);
  } else {
    console.log(`❌ Email not in admin whitelist`);
  }

  return isInWhitelist;
}

/**
 * Check if user has specific permission
 */
export function hasPermission(
  adminConfig: AdminConfig,
  resource: string,
  action: string
): boolean {
  if (!adminConfig.isAdmin) return false;

  const permission = adminConfig.permissions.find(
    (p) => p.resource === resource
  );
  return permission ? permission.actions.includes(action) : false;
}

/**
 * Development utilities for testing admin functionality
 */
export const DevAdminUtils = {
  /**
   * Toggle development admin mode (for testing)
   */
  toggleAdminMode: (): boolean => {
    DEV_ADMIN_MODE = !DEV_ADMIN_MODE;
    console.log(
      `🔧 Dev Admin Mode: ${DEV_ADMIN_MODE ? "ENABLED" : "DISABLED"}`
    );
    return DEV_ADMIN_MODE;
  },

  /**
   * Get current admin mode status
   */
  isAdminModeEnabled: (): boolean => {
    return DEV_ADMIN_MODE;
  },

  /**
   * Enable admin mode
   */
  enableAdminMode: (): void => {
    DEV_ADMIN_MODE = true;
    console.log("🔧 Dev Admin Mode: ENABLED");
  },

  /**
   * Disable admin mode
   */
  disableAdminMode: (): void => {
    DEV_ADMIN_MODE = false;
    console.log("🔧 Dev Admin Mode: DISABLED");
  },

  /**
   * Debug admin status for current user
   */
  debugAdminStatus: (userEmail?: string): void => {
    console.log("🔧 === ADMIN DEBUG INFO ===");
    console.log(`🔧 DEV_ADMIN_MODE: ${DEV_ADMIN_MODE}`);
    console.log(`🔧 ADMIN_EMAILS:`, APP_CONFIG.ADMIN_EMAILS);
    if (userEmail) {
      console.log(`🔧 Testing email: "${userEmail}"`);
      console.log(`🔧 Admin status: ${isUserAdmin(userEmail)}`);
    } else {
      console.log(`🔧 No email provided for testing`);
    }
    console.log("🔧 === END DEBUG INFO ===");
  },
};

// Make DevAdminUtils available globally for console access
if (typeof window !== "undefined") {
  (window as any).DevAdminUtils = DevAdminUtils;
}
